import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { downloadStoredFile } from "@/lib/storage-url";
import { useSignedStoredUrl } from "@/hooks/useSignedStoredUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, FileText, PenLine, X } from "lucide-react";
import TemplateFieldEditor, { type PlacedField } from "./TemplateFieldEditor";
import { SignatureDialog } from "@/components/tenant/SignatureDialog";
import {
  TemplateSectionCard,
  TemplateLoading,
  TemplateEmptyState,
  TemplateFieldReference,
  TemplateRowActions,
  DefaultBadge,
} from "./templates/TemplateSectionUI";
import { PdfUploadField } from "./templates/PdfUploadField";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";

// ── Standard field names the backend expects ────────────────────────
const EXPECTED_FIELDS = [
  // Tenant
  "TENANT_FULLNAME",
  "TENANT_EMAIL",
  "TENANT_PHONE_NUMBER",
  // Landlord / representative
  "OWNER_NAME",
  "REPRESENTOR_NAME",
  // Property address
  "PROPERTY_STREET_NAME",
  "PROPERTY_STREET_NUMBER",
  "PROPERTY_ZIP_CODE",
  "PROPERTY_CITY_NAME",
  "ROOM_NAME_NUMBER",
  // Lease dates & amounts
  "LEASE_START_DATE",
  "LEASE_END_DATE",
  "RENT_AMOUNT",
  "DEPOSIT_AMOUNT",
  // Bank details
  "ACCOUNT_NAME",
  "IBAN",
  "BIC",
  "BANK_NAME",
  // Signing — landlord side then tenant side (SIGNING_DATE appears twice)
  "REPRESENTOR_CITY",
  "SIGNING_DATE",
  "TENANT_SIGNING_CITY",
  "TENANT_SIGNING_DATE",
  // Signature image stamps — not text
  "REPRESENTOR_SIGNATURE",
  "TENANT_SIGNATURE",
] as const;

/**
 * Fields that MUST be placed on the signing page for the contract-signing
 * flow to work end-to-end. These live on the last page of every German
 * rental template:
 *   - REPRESENTOR_SIGNATURE — where create-contract stamps the landlord image
 *   - TENANT_SIGNATURE   — where DocuSign places the Sign button
 *   - SIGNING_DATE       — landlord date (drawn by PDF at generation)
 * TENANT_SIGNING_CITY is intentionally NOT required: the Ort Fill-In tab
 * is only emitted when that box is placed, so leaving it out is legitimate.
 * TENANT_SIGNING_DATE is also NOT required: the tenant signing date can be
 * drawn by the PDF at generation time even when the field isn't placed.
 */
const REQUIRED_SIGNING_FIELDS: readonly string[] = [
  "REPRESENTOR_SIGNATURE",
  "TENANT_SIGNATURE",
  "SIGNING_DATE",
];

/**
 * Field names that were renamed after templates had already been saved.
 * Stored configs keep the old name until the admin re-saves the template, so
 * placements are migrated on load — otherwise the renamed boxes would read as
 * unplaced and REQUIRED_SIGNING_FIELDS would block the save.
 * create-contract still honours the old names too, for templates never re-saved.
 */
const RENAMED_FIELDS: Record<string, string> = {
  LEASOR_NAME: "OWNER_NAME",
  LEASOR_SIGNATURE: "REPRESENTOR_SIGNATURE",
};

const withCurrentFieldNames = (fields: PlacedField[]): PlacedField[] =>
  fields.map((f) =>
    RENAMED_FIELDS[f.name] ? { ...f, name: RENAMED_FIELDS[f.name] } : f
  );

/** Sample values used to fill the preview PDF so the admin can verify placement. */
const SAMPLE_DATA: Record<string, string> = {
  TENANT_FULLNAME: "Max Mustermann",
  TENANT_EMAIL: "max@example.com",
  TENANT_PHONE_NUMBER: "+49 170 1234567",
  OWNER_NAME: "Coliviq GmbH",
  REPRESENTOR_NAME: "John Doe",
  PROPERTY_STREET_NAME: "Musterstraße",
  PROPERTY_STREET_NUMBER: "42",
  PROPERTY_ZIP_CODE: "10115",
  PROPERTY_CITY_NAME: "Berlin",
  ROOM_NAME_NUMBER: "Room A - Sunny",
  LEASE_START_DATE: "01.05.2026",
  LEASE_END_DATE: "31.10.2026",
  RENT_AMOUNT: "650.00",
  DEPOSIT_AMOUNT: "975.00",
  ACCOUNT_NAME: "Coliviq GmbH",
  IBAN: "DE89 3704 0044 0532 0130 00",
  BIC: "COBADEFFXXX",
  BANK_NAME: "Commerzbank",
  REPRESENTOR_CITY: "München",
  SIGNING_DATE: "15.04.2026",
  TENANT_SIGNING_CITY: "Berlin",
  TENANT_SIGNING_DATE: "15.04.2026",
  REPRESENTOR_SIGNATURE: "[Landlord signature]",
  TENANT_SIGNATURE: "[Tenant signature]",
};

type ContractTemplate = {
  id: string;
  name: string;
  description: string | null;
  template_pdf_url: string;
  fill_mode: string;
  vector_overlay_config: { fields: PlacedField[] } | null;
  is_default: boolean;
  leasor_signature_url: string | null;
  created_at: string;
  updated_at: string;
};

export function AdminContractTemplates() {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();

  // ── List state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ContractTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templatePendingDelete, setTemplatePendingDelete] =
    useState<ContractTemplate | null>(null);

  // ── Form state ──
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    fill_mode: "vector_overlay" as "form_fields" | "vector_overlay",
    template_pdf_url: "",
    is_default: false,
    leasor_signature_url: "" as string,
  });

  // ── Preview / validation state ──
  /** Raw bytes of the uploaded PDF (before filling). */
  const [rawPdfBytes, setRawPdfBytes] = useState<Uint8Array | null>(null);
  // The signature preview is an <img>; its src is a signed URL, never the
  // stored public one (COL-436).
  const landlordSignature = useSignedStoredUrl(
    formData.leasor_signature_url || null,
    "contracts"
  );
  /** Field positions from the visual editor. */
  const [placedFields, setPlacedFields] = useState<PlacedField[]>([]);
  /** Landlord signature capture modal state. */
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  /**
   * Names from REQUIRED_SIGNING_FIELDS that haven't been placed yet.
   * Drives both the inline warning under the Update button and the
   * disabled-state of the button itself so an admin can't save a
   * template that would break downstream contract generation.
   */
  const missingRequiredFields = useMemo(() => {
    const placed = new Set(placedFields.map((f) => f.name));
    return REQUIRED_SIGNING_FIELDS.filter((name) => !placed.has(name));
  }, [placedFields]);

  // ── Auto-load PDF when editing an existing template ──
  useEffect(() => {
    if (!dialogOpen || rawPdfBytes || !formData.template_pdf_url) return;
    let cancelled = false;
    (async () => {
      try {
        // Through the storage API, not the stored public URL: the contracts
        // bucket is going private (COL-436).
        const blob = await downloadStoredFile(
          formData.template_pdf_url,
          "contracts"
        );
        if (cancelled) return;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        if (cancelled) return;
        setRawPdfBytes(bytes);
      } catch {
        // Silently fail — user can click upload to retry
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, formData.template_pdf_url]);

  // ── Queries ──
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      // vector_overlay_config is jsonb; ContractTemplate asserts its shape.
      return data as unknown as ContractTemplate[];
    },
  });

  // ── Mutations ──
  const saveMutation = useMutation({
    mutationFn: async (payload: {
      id?: string;
      name: string;
      description: string | null;
      fill_mode: string;
      template_pdf_url: string;
      is_default: boolean;
      vector_overlay_config?: { fields: PlacedField[] } | null;
      leasor_signature_url?: string | null;
    }) => {
      if (payload.is_default) {
        await supabase
          .from("contract_templates")
          .update({ is_default: false })
          .eq("is_default", true);
      }
      if (payload.id) {
        const { error } = await supabase
          .from("contract_templates")
          .update({
            name: payload.name,
            description: payload.description,
            fill_mode: payload.fill_mode,
            template_pdf_url: payload.template_pdf_url,
            is_default: payload.is_default,
            vector_overlay_config: payload.vector_overlay_config ?? null,
            leasor_signature_url: payload.leasor_signature_url ?? null,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id", payload.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("contract_templates").insert({
          name: payload.name,
          description: payload.description,
          fill_mode: payload.fill_mode,
          template_pdf_url: payload.template_pdf_url,
          is_default: payload.is_default,
          vector_overlay_config: payload.vector_overlay_config ?? null,
          leasor_signature_url: payload.leasor_signature_url ?? null,
        } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success(
        editingTemplate
          ? t("admin.templates.toastUpdated", "Template updated")
          : t("admin.templates.toastCreated", "Template created")
      );
      closeDialog();
    },
    onError: (err: Error) => {
      toast.error(
        `${t("admin.contractTemplates.saveFailedPrefix", "Failed to save template")}: ${err.message}`
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contract_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success(t("admin.templates.toastDeleted", "Template deleted"));
    },
    onError: (err: Error) => {
      toast.error(
        `${t("admin.contractTemplates.deleteFailedPrefix", "Failed to delete")}: ${err.message}`
      );
    },
  });

  // ── Helpers ──
  function resetState() {
    setRawPdfBytes(null);
    setPlacedFields([]);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingTemplate(null);
    resetState();
    setFormData({
      name: "",
      description: "",
      fill_mode: "vector_overlay",
      template_pdf_url: "",
      is_default: false,
      leasor_signature_url: "",
    });
  }

  function openCreate() {
    setEditingTemplate(null);
    resetState();
    setFormData({
      name: "",
      description: "",
      fill_mode: "vector_overlay",
      template_pdf_url: "",
      is_default: false,
      leasor_signature_url: "",
    });
    setDialogOpen(true);
  }

  function openEdit(tpl: ContractTemplate) {
    setEditingTemplate(tpl);
    resetState();
    // Load previously placed fields if any
    if (tpl.vector_overlay_config?.fields) {
      setPlacedFields(withCurrentFieldNames(tpl.vector_overlay_config.fields));
    }
    setFormData({
      name: tpl.name,
      description: tpl.description ?? "",
      fill_mode: "vector_overlay",
      template_pdf_url: tpl.template_pdf_url,
      is_default: tpl.is_default,
      leasor_signature_url: tpl.leasor_signature_url ?? "",
    });
    setDialogOpen(true);
  }

  function openDeleteConfirm(tpl: ContractTemplate) {
    setTemplatePendingDelete(tpl);
    setDeleteDialogOpen(true);
  }

  function closeDeleteConfirm() {
    if (deleteMutation.isPending) return;
    setDeleteDialogOpen(false);
    setTemplatePendingDelete(null);
  }

  function confirmDelete() {
    if (!templatePendingDelete) return;
    deleteMutation.mutate(templatePendingDelete.id, {
      onSettled: () => closeDeleteConfirm(),
    });
  }

  /** Upload a drawn/uploaded landlord signature PNG and stash the URL in formData. */
  async function handleTemplateSignatureConfirm(pngBlob: Blob) {
    setUploadingSignature(true);
    try {
      const templateSegment = editingTemplate?.id ?? "new";
      const fileName = `template-signatures/${templateSegment}/${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, pngBlob, { contentType: "image/png", upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({
        ...prev,
        leasor_signature_url: urlData.publicUrl,
      }));
      setSignatureDialogOpen(false);
      toast.success(
        t(
          "admin.templates.toastSignatureSaved",
          "Signature saved. Click Update/Create to persist on template."
        )
      );
    } catch (err: any) {
      toast.error(
        err?.message ||
          t(
            "admin.templates.toastSignatureFailed",
            "Failed to upload signature."
          )
      );
    } finally {
      setUploadingSignature(false);
    }
  }

  // ── PDF Upload ──
  async function handleFileUpload(file: File) {
    setUploading(true);
    resetState();
    try {
      // Read file bytes locally
      const arrayBuf = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuf);
      setRawPdfBytes(bytes);

      // Upload to Supabase Storage
      const fileName = `contract-templates/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, file, {
          contentType: "application/pdf",
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({ ...prev, template_pdf_url: urlData.publicUrl }));

      toast.success(t("admin.templates.toastPdfUploaded", "PDF uploaded"));
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit() {
    if (!formData.name.trim()) {
      toast.error(
        t("admin.templates.errNameRequired", "Template name is required")
      );
      return;
    }
    if (!formData.template_pdf_url.trim()) {
      toast.error(
        t(
          "admin.templates.errPdfRequired",
          "Template PDF is required — upload a file or paste a URL"
        )
      );
      return;
    }
    if (missingRequiredFields.length > 0) {
      // Safety net — the button is already disabled in this state, but
      // guard the save path in case the state was stale mid-click.
      toast.error(
        `Place these on the signing page before saving: ${missingRequiredFields.join(", ")}`
      );
      return;
    }
    saveMutation.mutate({
      id: editingTemplate?.id,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      fill_mode: formData.fill_mode,
      template_pdf_url: formData.template_pdf_url.trim(),
      is_default: formData.is_default,
      vector_overlay_config:
        placedFields.length > 0 ? { fields: placedFields } : null,
      leasor_signature_url: formData.leasor_signature_url.trim() || null,
    });
  }

  // ── Render ──
  return (
    <>
      <TemplateSectionCard
        icon={FileText}
        title={t("admin.templates.sectionTitle", "Contract Templates")}
        description={t(
          "admin.templates.sectionDesc",
          "Manage PDF templates used for contract generation. Each template should contain named form fields (TENANT_NAME, ADDRESS, MONTHLY_RENT, etc.)."
        )}
        onAdd={openCreate}
      >
        {isLoading ? (
          <TemplateLoading />
        ) : templates.length === 0 ? (
          <TemplateEmptyState
            icon={FileText}
            title={t(
              "admin.templates.emptyTitle",
              "No templates yet. Add your first contract template."
            )}
            hint={t(
              "admin.templates.emptyDesc",
              "The system will fall back to the CONTRACT_TEMPLATE_PDF_URL environment variable until a template is added."
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>{t("admin.templates.colName", "Name")}</TableHead>
                  <TableHead>
                    {t("admin.templates.colDefault", "Default")}
                  </TableHead>
                  <TableHead>
                    {t("admin.templates.colCreated", "Created")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("admin.templates.colActions", "Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{tpl.name}</span>
                        {tpl.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {tpl.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{tpl.is_default && <DefaultBadge />}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateNumeric(tpl.created_at, language)}
                    </TableCell>
                    <TableCell className="text-right">
                      <TemplateRowActions
                        pdfUrl={tpl.template_pdf_url}
                        downloadName={
                          tpl.name ? `${tpl.name}.pdf` : "contract-template.pdf"
                        }
                        onEdit={() => openEdit(tpl)}
                        onDelete={() => openDeleteConfirm(tpl)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <TemplateFieldReference
          title={t(
            "admin.templates.fieldNamesTitle",
            "Standard Form Field Names"
          )}
          description={t(
            "admin.templates.fieldNamesDesc",
            "When creating a fillable PDF template (e.g. in Adobe Acrobat or LibreOffice), add text fields with these exact names:"
          )}
          fields={EXPECTED_FIELDS}
        />
      </TemplateSectionCard>

      {/* ── Create / Edit Dialog — unified layout ── */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent
          className={
            rawPdfBytes ? "max-w-6xl max-h-[92vh] overflow-y-auto" : "max-w-lg"
          }
        >
          <DialogHeader>
            <DialogTitle>
              {editingTemplate
                ? t("admin.templates.dialogEditTitle", "Edit Contract Template")
                : t("admin.templates.dialogNewTitle", "New Contract Template")}
            </DialogTitle>
          </DialogHeader>

          <div
            className={rawPdfBytes ? "grid grid-cols-[320px_1fr] gap-6" : ""}
          >
            {/* ── Left: Form settings ── */}
            <div className="space-y-4 mt-1">
              <div className="space-y-1.5">
                <Label htmlFor="tpl-name">
                  {t("admin.templates.fieldName", "Template Name *")}
                </Label>
                <Input
                  id="tpl-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t(
                    "admin.templates.namePlaceholder",
                    "e.g. Standard DE, Short-term EN"
                  )}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tpl-desc">
                  {t("admin.templates.fieldDescription", "Description")}
                </Label>
                <Textarea
                  id="tpl-desc"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder={t(
                    "admin.templates.descPlaceholder",
                    "Optional notes about this template"
                  )}
                  rows={2}
                />
              </div>

              <PdfUploadField
                value={formData.template_pdf_url}
                onValueChange={(v) =>
                  setFormData({ ...formData, template_pdf_url: v })
                }
                onFile={handleFileUpload}
                uploading={uploading}
                loadingExisting={!!formData.template_pdf_url && !rawPdfBytes}
              />

              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.templates.dragHint",
                  "Drag fields onto the PDF preview to position them."
                )}
              </p>

              {/* ── Default landlord signature (template-level fallback) ── */}
              <div className="space-y-1.5">
                <Label>
                  {t("admin.templates.landlordSignature", "Landlord Signature")}
                </Label>
                {formData.leasor_signature_url ? (
                  <div className="rounded-lg border bg-muted/20 p-2 flex items-center gap-2">
                    {landlordSignature.failed ? (
                      <span className="text-xs text-destructive">
                        {t(
                          "admin.templates.signatureLoadFailed",
                          "Could not load the signature image."
                        )}
                      </span>
                    ) : (
                      <img
                        src={landlordSignature.url ?? undefined}
                        alt={t(
                          "admin.templates.landlordSignatureAlt",
                          "Landlord signature"
                        )}
                        className="h-14 w-auto max-w-[160px] rounded-md bg-white border object-contain"
                      />
                    )}
                    <div className="flex-1 flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setSignatureDialogOpen(true)}
                        disabled={uploadingSignature}
                      >
                        <PenLine className="w-3 h-3 mr-1" />
                        {t("admin.contractTemplates.replaceButton", "Replace")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            leasor_signature_url: "",
                          }))
                        }
                        disabled={uploadingSignature}
                      >
                        <X className="w-3 h-3 mr-1" />
                        {t("admin.contractTemplates.removeButton", "Remove")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setSignatureDialogOpen(true)}
                    disabled={uploadingSignature}
                  >
                    {uploadingSignature ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <PenLine className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {t(
                      "admin.contractTemplates.drawOrUploadSignature",
                      "Draw or upload signature"
                    )}
                  </Button>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "admin.contractTemplates.representorSignatureHint",
                    "Stamped at the REPRESENTOR_SIGNATURE position when a property has no signature of its own."
                  )}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tpl-default"
                  checked={formData.is_default}
                  onChange={(e) =>
                    setFormData({ ...formData, is_default: e.target.checked })
                  }
                  className="rounded"
                />
                <Label htmlFor="tpl-default" className="text-sm font-normal">
                  {t("admin.templates.setDefault", "Set as system default")}
                </Label>
              </div>

              {missingRequiredFields.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <div className="font-semibold mb-0.5">
                    {t(
                      "admin.templates.missingFieldsTitle",
                      "Missing required signing-page fields:"
                    )}
                  </div>
                  <div className="font-mono">
                    {missingRequiredFields.join(", ")}
                  </div>
                  <div className="mt-1 opacity-80">
                    {t(
                      "admin.templates.missingFieldsHint",
                      "Drop each onto the last PDF page where the template shows the signing block."
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={closeDialog}>
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    saveMutation.isPending || missingRequiredFields.length > 0
                  }
                  title={
                    missingRequiredFields.length > 0
                      ? t(
                          "admin.templates.placeRequiredFieldsFirst",
                          "Place required fields first: {fields}"
                        ).replace("{fields}", missingRequiredFields.join(", "))
                      : undefined
                  }
                >
                  {saveMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  )}
                  {editingTemplate
                    ? t("common.update", "Update")
                    : t("common.create", "Create")}
                  {placedFields.length > 0 && (
                    <span className="ml-1 text-xs opacity-70">
                      (
                      {t(
                        "admin.templates.fieldsCount",
                        "{count} fields"
                      ).replace("{count}", String(placedFields.length))}
                      )
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* ── Right: Visual field placement editor ── */}
            {rawPdfBytes && (
              <div className="flex flex-col min-h-0">
                <TemplateFieldEditor
                  pdfBytes={rawPdfBytes}
                  initialFields={placedFields}
                  onChange={setPlacedFields}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("admin.templates.deleteTitle", "Delete template?")}
            </DialogTitle>
            <DialogDescription>
              {templatePendingDelete
                ? t(
                    "admin.templates.deleteDescNamed",
                    'This will permanently delete "{name}".'
                  ).replace("{name}", templatePendingDelete.name)
                : t(
                    "admin.templates.deleteDesc",
                    "This will permanently delete this template."
                  )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={!templatePendingDelete || deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              )}
              {t("admin.templates.deleteButton", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Landlord signature capture (draw / upload) ── */}
      <SignatureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onConfirm={handleTemplateSignatureConfirm}
        submitting={uploadingSignature}
        language="en"
      />
    </>
  );
}
